import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {useIsraeliVatEligibility} from 'hooks/useIsraeliVatEligibility'
import {getAllTaxesIncluded} from 'modules/meta/selectors'
import {
  getAnchorPriceNightlyPerRoom,
  getNumberOfRooms,
  getSearchParameters,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'

import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Offer} from '@vio/core/src/offer/types/offer'
import {getRoomOfferPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {getLengthOfStay} from '@vio/core/src/utils/date'
import {getRoundingStrategy, RoundingStrategy} from '@vio/core/src/utils/number'
import {Price} from '@vio/shared/src/search/Price'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'
import {PointsPrice} from '@vio/shared/src/whiteLabel/PointsPrice'

import {priceSuffixMessages, RoomOfferPriceDataId} from './constants'

interface Props {
  hotelId: string
  offer: Offer
  onClickPriceDetails: () => void
  shouldShowDealFreezeButton?: boolean
  isCompact?: boolean
  isPrivate?: boolean
  isVioTagVisible?: boolean
  roundingStrategy?: RoundingStrategy
  isOfferSoldOut?: boolean
  // Hide the secondary price-details line (total/nightly breakdown button
  // or taxes-included copy) - used in space-constrained layouts.
  hidePriceDetails?: boolean
}

const PostFix = styled(Text)<{isCompact?: boolean; isOfferSoldOut?: boolean}>`
  white-space: nowrap;
  margin-left: ${cssTheme.layout.spacing.s200};
  color: ${({isCompact, isOfferSoldOut}) =>
    isOfferSoldOut
      ? cssTheme.colors.content.neutral.disabled
      : isCompact
        ? cssTheme.colors.content.neutral.primary
        : cssTheme.colors.content.neutral.secondary};
`

const PriceDetailsButton = styled(ButtonLink)`
  padding-left: 0;
  padding-right: 0;
  text-align: end;
`

const SecondaryPriceWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  white-space: pre-wrap;
  justify-content: flex-end;
`
const FlexWrapper = styled.div`
  display: flex;
`

const TaxDisplay = styled.div`
  &:first-letter {
    text-transform: capitalize;
  }
`

const roomsThreshold = 1

export const PriceDetailsButtonWithTax = ({
  onClick,
  hotelId,
  children,
  isOfferSoldOut
}: {
  onClick: () => void
  hotelId: string
  children?: React.ReactNode
  isOfferSoldOut?: boolean
}) => {
  const {formatMessage} = useIntl()
  const {hasAdditionalCharges} = useIsraeliVatEligibility(hotelId)
  const includeTaxes = useSelector(getAllTaxesIncluded)
  const showTotalPrices = useSelector(getShowTotalPrices)

  const taxMessage = hasAdditionalCharges
    ? priceSuffixMessages.additionalCharges
    : includeTaxes || showTotalPrices
      ? priceSuffixMessages.withTaxesAndFees
      : priceSuffixMessages.includingTaxes

  const priceDetailsContent = (
    <Text
      tag="div"
      variant="labelS"
      dataId="includingTaxes"
      colorPath={isOfferSoldOut ? 'content.neutral.disabled' : null}
    >
      {children}
      <TaxDisplay>{formatMessage(taxMessage)}</TaxDisplay>
    </Text>
  )

  return isOfferSoldOut ? (
    priceDetailsContent
  ) : (
    <PriceDetailsButton
      variant="secondary"
      isCompact
      onClick={onClick}
      size="sm"
      dataId="RoomPriceDetail"
    >
      {priceDetailsContent}
    </PriceDetailsButton>
  )
}

export const RoomOfferPrice = ({
  hotelId,
  offer,
  onClickPriceDetails,
  isCompact = false,
  isPrivate = false,
  isVioTagVisible = false,
  roundingStrategy = getRoundingStrategy(),
  isOfferSoldOut = false,
  hidePriceDetails = false
}: Props) => {
  const {formatMessage} = useIntl()
  const points = usePointsPriceDisplay()
  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })
  const anchorPrice = useSelector(state =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))

  const showTotalPrices = useSelector(getShowTotalPrices)
  const {checkIn, checkOut} = useSelector(getSearchParameters) || {}

  const prices = getRoomOfferPrices(offer.prices, {
    numberOfRooms,
    anchorNightly: anchorPrice,
    anchorTotal: anchorPriceTotal
  })
  if (!prices) return null

  const {currencyCode} = prices
  const mainPrice = showTotalPrices ? prices.total : prices.nightly
  const detailsPrice = showTotalPrices ? prices.nightly : prices.total

  const pricePostFix = showTotalPrices
    ? priceSuffixMessages.total
    : priceSuffixMessages.perNight
  const priceDetailPostFix = showTotalPrices
    ? priceSuffixMessages.perNight
    : priceSuffixMessages.total
  const isMultiRoom = numberOfRooms > roomsThreshold

  const isMainPriceDifferentFromDetailsPrice =
    mainPrice.price !== detailsPrice.price

  const lengthOfStay = checkIn && checkOut && getLengthOfStay(checkIn, checkOut)

  const soldOutColor = isOfferSoldOut
    ? cssTheme.colors.content.neutral.disabled
    : null

  const mainPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.Total
    : RoomOfferPriceDataId.PerNight

  const detailsPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.PerNight
    : RoomOfferPriceDataId.Total

  const renderPriceDetailsButton = () => {
    if (hidePriceDetails) {
      return null
    }

    if (
      !isMainPriceDifferentFromDetailsPrice &&
      lengthOfStay === 1 &&
      !isMultiRoom
    ) {
      return (
        <PriceDetailsButtonWithTax
          hotelId={hotelId}
          onClick={onClickPriceDetails}
          isOfferSoldOut={isOfferSoldOut}
        />
      )
    }

    if ((isMainPriceDifferentFromDetailsPrice || isMultiRoom) && !isCompact) {
      return (
        <PriceDetailsButtonWithTax
          hotelId={hotelId}
          onClick={onClickPriceDetails}
          isOfferSoldOut={isOfferSoldOut}
        >
          <SecondaryPriceWrapper>
            <FlexWrapper>
              <Price
                currency={currencyCode}
                value={detailsPrice.price}
                roundingStrategy={roundingStrategy}
                noStyle
                dataId={detailsPriceDataId}
                style={{
                  color: soldOutColor
                }}
              />
              <Box
                display="flex"
                whiteSpace="nowrap"
                alignItems="baseline"
                justifyContent="flex-start"
              >
                &nbsp;
                {formatMessage(priceDetailPostFix)}
                {isMultiRoom && <>,&nbsp;</>}
              </Box>
            </FlexWrapper>
            {isMultiRoom &&
              formatMessage(priceSuffixMessages.numberOfRoomsMessage, {
                count: numberOfRooms
              })}
          </SecondaryPriceWrapper>
        </PriceDetailsButtonWithTax>
      )
    }

    return null
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      alignItems={isCompact ? 'flex-start' : 'flex-end'}
      justifyContent="center"
    >
      {points.isOn && isCompact ? (
        <PointsPrice
          {...points.splitParts(mainPrice.price)}
          currencyCode={currencyCode}
          showMaxPointsCaption={false}
          isMuted={isOfferSoldOut}
          dataId={mainPriceDataId}
          alignment="start"
          suffix={
            <Text tag="span" variant={showTotalPrices ? 'bodyS' : 'bodyXS'}>
              {formatMessage(pricePostFix)}
            </Text>
          }
        />
      ) : (
        <Box
          display="flex"
          maxWidth={isCompact ? '100%' : '140px'}
          whiteSpace="nowrap"
          alignItems="baseline"
          justifyContent={isCompact ? 'flex-start' : 'flex-end'}
          columnGap="s200"
          flexWrap="wrap"
        >
          {isVioTagVisible && mainPrice.showAnchor && (
            <Price
              isAnchor
              currency={currencyCode}
              value={mainPrice.anchor}
              roundingStrategy={roundingStrategy}
              style={{
                color: soldOutColor
              }}
            />
          )}
          <Box
            display="flex"
            whiteSpace="normal"
            alignItems="baseline"
            justifyContent={isCompact ? 'flex-start' : 'flex-end'}
            flexWrap={isCompact ? 'wrap' : 'nowrap'}
          >
            <Price
              isPrivate={isPrivate || isVioTagVisible}
              currency={currencyCode}
              value={mainPrice.price}
              roundingStrategy={roundingStrategy}
              size="lg"
              dataId={mainPriceDataId}
              style={{
                color: soldOutColor
              }}
            />
            <PostFix
              variant={showTotalPrices || !isCompact ? 'bodyS' : 'bodyXS'}
              isOfferSoldOut={isOfferSoldOut}
              isCompact={isCompact}
            >
              {formatMessage(pricePostFix)}
            </PostFix>
          </Box>
        </Box>
      )}

      {renderPriceDetailsButton()}
    </Box>
  )
}
